import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';
import type { AudioVideoObserver } from 'amazon-chime-sdk-js';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb } from '../../components/Breadcrumb';
import { apiClient } from '../../utils/apiClient';

// オンライン診療ページ（Figma準拠、静的プレースホルダー）
const OnlineMeetingPage: React.FC = () => {
  const { meetingId: meetingIdParam } = useParams<{ meetingId?: string }>();

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinInfo, setJoinInfo] = useState<{ attendeeId: string; joinToken: string } | null>(null);
  const [_meetingSession, setMeetingSession] = useState<DefaultMeetingSession | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [remoteTileIds, setRemoteTileIds] = useState<number[]>([]);
  const remoteVideoRefs = useRef<Record<number, HTMLVideoElement | null>>({});

  const canJoin = !!meetingIdParam && !joining;

  const handleJoinMeeting = async () => {
    if (!meetingIdParam) return;
    setJoining(true);
    setJoinError(null);
    try {
      const randomUserId = `test-${Math.random().toString(36).slice(2, 10)}`;
      const url = `/api/v1/meetings/${encodeURIComponent(meetingIdParam)}/attendees?userId=${encodeURIComponent(randomUserId)}`;
      const data = (await apiClient(url, { method: 'POST' })) as {
        Meeting: {
          MeetingId: string;
          ExternalMeetingId: string;
          MediaRegion: string;
          MediaPlacement: Record<string, string>;
        };
        Attendee: { AttendeeId: string; JoinToken: string; ExternalUserId: string };
      };

      const configuration = new MeetingSessionConfiguration(
        { Meeting: data.Meeting },
        { Attendee: { AttendeeId: data.Attendee.AttendeeId, JoinToken: data.Attendee.JoinToken } }
      );
      const logger = new ConsoleLogger('ChimeMeeting', LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);
      const session = new DefaultMeetingSession(configuration, logger, deviceController);

      // リモート音声の出力先をバインド
      if (audioRef.current) {
        audioRef.current.muted = false;
        session.audioVideo.bindAudioElement(audioRef.current);
      }

      // 送話は常にオン（自分の声を他参加者へ届ける）。
      // Chime はローカル音声のループバック再生を行わないため、
      // 自分では聞こえず、他参加者の音声のみが audio 要素から再生されます。
      const audioInputs = await session.deviceController.listAudioInputDevices();
      if (audioInputs.length > 0) {
        await session.deviceController.startAudioInput(audioInputs[0].deviceId ?? null);
      }
      const videoInputs = await session.deviceController.listVideoInputDevices();
      if (videoInputs.length > 0) {
        await session.deviceController.startVideoInput(videoInputs[0].deviceId ?? null);
      }

      const observer: AudioVideoObserver = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        videoTileDidUpdate: (tile: any) => {
          const tileId = tile.tileId as number | undefined;
          if (!tileId) return;

          if (tile.localTile && localVideoRef.current) {
            session.audioVideo.bindVideoElement(tileId, localVideoRef.current);
            return;
          }

          if (!tile.localTile && !tile.isContent) {
            setRemoteTileIds((prev) => (prev.includes(tileId) ? prev : [...prev, tileId]));
            const el = remoteVideoRefs.current[tileId];
            if (el) {
              session.audioVideo.bindVideoElement(tileId, el);
            }
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        videoTileWasRemoved: (tileId: any) => {
          const id = tileId as number;
          setRemoteTileIds((prev) => prev.filter((t) => t !== id));
          const el = remoteVideoRefs.current[id];
          if (el) {
            session.audioVideo.unbindVideoElement(id);
            remoteVideoRefs.current[id] = null;
          }
        },
      };
      session.audioVideo.addObserver(observer);

      session.audioVideo.start();
      session.audioVideo.startLocalVideoTile();

      setMeetingSession(session);
      setJoinInfo({ attendeeId: data.Attendee.AttendeeId, joinToken: data.Attendee.JoinToken });
    } catch (err) {
      const status = (err as unknown as { status?: number })?.status;
      const msg =
        status === 404
          ? 'ミーティングが存在しません。予約IDからミーティングを作成してから再度お試しください。'
          : err instanceof Error
            ? err.message
            : '参加に失敗しました';
      setJoinError(msg);
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    // meetingIdがある場合、UI上で参加導線を表示。自動参加したければ以下を有効化。
    // if (meetingIdParam) handleJoinMeeting();
  }, [meetingIdParam]);

  // ページ離脱（アンマウント）やルート変更時にセッションをクリーンアップして通話から抜ける
  useEffect(() => {
    return () => {
      try {
        if (_meetingSession) {
          // 映像/音声の停止とオブザーバ削除
          _meetingSession.audioVideo.stopLocalVideoTile();
          _meetingSession.audioVideo.stop();
          // デバイス入力の停止
          _meetingSession.deviceController.stopAudioInput();
          _meetingSession.deviceController.stopVideoInput();
        }
      } catch {
        // 失敗しても破棄を続ける
      }
    };
  }, [_meetingSession]);

  return (
    <div className="flex flex-col gap-[40px]" aria-label="オンライン診療ページ">
      {/* パンくず */}
      <Breadcrumb
        items={[
          { label: 'ホーム', href: '/dashboard' },
          { label: 'スケジュール' },
          { label: '鈴木太郎さんの診察ルーム' },
        ]}
        separator="＜"
        className="text-[14px]"
        showLeadingSeparator={true}
      />

      {/* ヘッダー（患者情報） */}
      <section className="flex items-center gap-[20px]">
        <div
          className="flex items-center justify-center bg-[#5a5a5a] rounded-full w-[64px] h-[64px]"
          aria-hidden
        >
          <div className="bg-white rounded-full w-[40px] h-[40px]" />
        </div>
        <div className="flex flex-col gap-[12px] min-w-[600px]">
          <div className="flex items-center gap-[40px]">
            <h1 className="text-[34px] leading-none">鈴木太郎</h1>
            <span className="text-[16px]">サカタのタネ株式会社</span>
            <button
              className="flex items-center gap-[5px] text-[#ff7a7a] text-[16px]"
              aria-label="編集"
            >
              <span className="inline-block w-[20px] h-[20px] border border-[#ff7a7a] rounded-sm" />
              編集
            </button>
          </div>
          <div className="flex gap-[40px] text-[14px] text-black">
            <span>taro_suzuki@gmail.com</span>
            <span>090-7531-1059</span>
            <span>〒100-25 大阪府大阪市北区天満橋2-5-1 グランドパーク1005</span>
          </div>
        </div>
      </section>

      {/* 日付とビュー切替 */}
      <section className="flex flex-col gap-[20px] w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-[10px] h-[68px]">
            <p className="text-[22px]">2025年08月29日 金曜日</p>
            <div className="flex items-center gap-[6px]">
              <button className="w-[32px] h-[32px] rounded-full border border-[#ff7a7a] text-[#ff7a7a]">
                〈
              </button>
              <button className="w-[32px] h-[32px] rounded-full border border-[#ff7a7a] text-[#ff7a7a]">
                〉
              </button>
            </div>
          </div>
          <div className="flex items-center gap-[10px] h-[68px]">
            <button className="px-[14px] py-[10px] rounded-[20px] border border-black flex items-center gap-[5px]">
              <span className="inline-block w-[20px] h-[20px] border border-black" />月
            </button>
            <button className="px-[14px] py-[10px] rounded-[20px] border border-black flex items-center gap-[9px]">
              <span className="inline-block w-[20px] h-[20px] border border-black" />週
            </button>
            <button className="px-[14px] py-[10px] rounded-[20px] bg-[#cecece] flex items-center gap-[5px]">
              <span className="inline-block w-[20px] h-[20px] border border-black" />日
            </button>
          </div>
        </div>

        {/* メイン2カラム */}
        <div className="flex gap-[40px] w-full">
          {/* 左: 診療ビュー */}
          <div className="flex-1 flex flex-col gap-[20px]">
            <div className="bg-[#434343] border border-[#d9d9d9] rounded-[8px] h-[592px] p-[24px] text-white">
              <div className="flex gap-[20px]">
                <span className="text-[24px] text-black">9:00</span>
                <span className="text-[20px] text-[#b3b3b3]">オンライン診療画面</span>
              </div>
              {/* 映像エリアのプレースホルダー */}
              <div className="mt-[20px] h-[500px] rounded-[8px] bg-[#3a3a3a] flex items-center justify-center">
                {/* リモート音声再生用の不可視オーディオ要素 */}
                <audio ref={audioRef} className="hidden" aria-hidden />
                {/* 参加UI */}
                <div className="flex flex-col items-center gap-[12px]">
                  <div className="text-white text-[16px]">
                    {meetingIdParam ? (
                      <span>ミーティングID: {meetingIdParam}</span>
                    ) : (
                      <span>ミーティングIDがURLにありません</span>
                    )}
                  </div>
                  <button
                    disabled={!canJoin}
                    onClick={handleJoinMeeting}
                    className={`px-[14px] py-[10px] rounded-[20px] ${canJoin ? 'bg-[#ff7a7a] text-white' : 'bg-[#777777] text-[#dddddd]'} text-[16px]`}
                    aria-label="ミーティングに参加"
                  >
                    {joining ? '参加処理中...' : 'ミーティングに参加'}
                  </button>
                  {joinError && <div className="text-[#ff7a7a] text-[14px]">{joinError}</div>}
                  {joinInfo && (
                    <div className="text-white text-[14px] break-all max-w-[520px]">
                      <div>参加者ID: {joinInfo.attendeeId}</div>
                      <div className="mt-[6px]">joinToken: {joinInfo.joinToken}</div>
                      <div className="mt-[10px] text-[#cecece]">
                        ※ このトークンはクライアントSDK初期化に使用します
                      </div>
                      <div className="mt-[14px] w-full">
                        <div className="grid grid-cols-2 gap-4">
                          <video
                            ref={localVideoRef}
                            className="w-[480px] h-[270px] bg-black"
                            autoPlay
                            playsInline
                            muted
                          />
                          {remoteTileIds.map((tileId) => (
                            <video
                              key={tileId}
                              ref={(el) => {
                                remoteVideoRefs.current[tileId] = el;
                                if (_meetingSession && el) {
                                  _meetingSession.audioVideo.bindVideoElement(tileId, el);
                                }
                              }}
                              className="w-[480px] h-[270px] bg-black"
                              autoPlay
                              playsInline
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 所見ボタン群 */}
            <div className="flex items-center justify-end gap-[12px]">
              {['所見なし', '要経過観察', '要治療', '治療推奨'].map((label) => (
                <button
                  key={label}
                  className="px-[14px] py-[10px] rounded-[20px] bg-[#ff7a7a] text-white text-[20px] flex items-center gap-[5px]"
                >
                  <span className="inline-block w-[20px] h-[20px] rounded-sm border border-white" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 右: ドキュメントカード */}
          <div className="flex flex-col gap-[40px] justify-center">
            {[{ t: '検査結果' }, { t: '問診票PDF' }].map((item, idx) => (
              <div
                key={idx}
                className="relative bg-white border border-[#bcbcbc] rounded-[10px] w-[390px] h-[400px] flex items-center justify-center gap-[10px]"
              >
                <span className="inline-block w-[20px] h-[20px] border border-[#5a5a5a]" />
                <span className="text-[16px]">{item.t}</span>
                <span className="absolute top-[-8px] right-[21px] w-[14px] h-[14px] bg-[#ff7a7a] rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* カルテ */}
      <section className="w-full border border-[rgba(0,0,0,0.5)] px-[40px] py-[30px]">
        <div className="grid grid-cols-2 h-[37px]">
          <h2 className="text-[22px]">カルテ</h2>
          <div className="text-right text-[#5a5a5a] text-[16px]">2025.08.20</div>
        </div>
        <div className="mt-[10px] text-[16px]">
          <p className="mb-0">
            --------------------------------------------------------------------------------------------------------------------
          </p>
          <p className="mb-0">鈴木太郎さんはPSG検査の結果、陽性と判定されました。</p>
          <p className="mb-0">速やかにCPAP治療に移行してください。</p>
          <p>
            --------------------------------------------------------------------------------------------------------------------
          </p>
        </div>
      </section>
    </div>
  );
};

export default OnlineMeetingPage;
